<template>
  <v-page>
    <toolbar fixed>
      <template v-slot:left>
        <span class="v-toolbar__text">Subscriptions</span>
      </template>
    </toolbar>

    <template v-if="pastDueSubscriptions.length > 0">
      <div
        class="my-2"
        v-for="subscription in pastDueSubscriptions"
        :key="subscription.id"
      >
        <list boxed heading="Overdue Subscriptions">
          <list-item>
            <template slot="left">
              Product:
            </template>

            <template slot="right">
              <span class="v-list-item__title">
                {{ subscription.product.title }}
              </span>
            </template>
          </list-item>
          <list-item v-if="!subscription.canceled">
            <template slot="left">
              Amount:
            </template>

            <template slot="right">
              <span class="v-list-item__title">
                {{ subscription.product.symbol
                }}{{ subscription.product.price }} /
                {{ subscription.product.interval }}
              </span>
            </template>
          </list-item>

          <template>
            <list-item>
              <template slot="left">
                Next Payment Attempt:
              </template>

              <template slot="right">
                <span class="v-list-item__title">
                  {{ toDate(subscription.next_payment_attempt) }}
                </span>
              </template>
            </list-item>
          </template>

          <div class="p-2">
            <v-button
              primary
              block
              :is-loading="isRetrying"
              @click="() => retryPayment(subscription)"
            >
              Retry Payment
            </v-button>
            <v-button
              secondary
              block
              class="mt-2"
              :disabled="isRetrying"
              @click="() => promptUpdateMethod(subscription, true)"
            >
              Update Payment Method
            </v-button>
          </div>
        </list>

        <div v-if="!subscription.canceled" class="w-100 text-center">
          <v-button
            secondary
            ghost
            small
            class="mt-2"
            :disabled="isRetrying"
            @click="() => promptToCancel(subscription)"
          >
            Cancel
          </v-button>
        </div>
      </div>
    </template>

    <template v-if="isLoading">
      <list class="mt-2" boxed>
        <div class="d-flex justify-content-center py-4">
          <v-loader />
        </div>
      </list>
    </template>
    <template v-else-if="subscriptions.length > 0">
      <div
        :class="[{ 'opacity-40': subscription.canceled }, 'my-2']"
        v-for="subscription in subscriptions"
        :key="subscription.id"
      >
        <list boxed :heading="subscription.product.title">
          <list-item>
            <template slot="left">
              Product:
            </template>

            <template slot="right">
              <span class="v-list-item__title">
                {{ subscription.product.title }}
              </span>
            </template>
          </list-item>
          <list-item v-if="!subscription.canceled">
            <template slot="left">
              Amount:
            </template>

            <template slot="right">
              <span class="v-list-item__title">
                {{ subscription.product.symbol
                }}{{ subscription.product.price }} /
                {{ subscription.product.interval }}
              </span>
            </template>
          </list-item>
          <list-item>
            <template slot="left">
              Started:
            </template>

            <template slot="right">
              <span class="v-list-item__title">
                {{ toDate(subscription.started_at) }}
              </span>
            </template>
          </list-item>

          <template v-if="subscription.canceled">
            <list-item>
              <template slot="left">
                Canceled At:
              </template>

              <template slot="right">
                <span class="v-list-item__title">
                  {{ toDate(subscription.canceled_at) }}
                </span>
              </template>
            </list-item>

            <list-item>
              <template slot="left">
                Expires:
              </template>

              <template slot="right">
                <span class="v-list-item__title">
                  {{ toDate(subscription.current_period_end) }}
                </span>
              </template>
            </list-item>
          </template>
          <template v-else>
            <list-item>
              <template slot="left">
                Next Payment Due:
              </template>

              <template slot="right">
                <span class="v-list-item__title">
                  {{ toDate(subscription.current_period_end) }}
                </span>
              </template>
            </list-item>
          </template>
        </list>

        <div v-if="!subscription.canceled" class="w-100 text-center">
          <v-button
            secondary
            ghost
            small
            class="mt-2"
            @click="() => promptToCancel(subscription)"
          >
            Cancel
          </v-button>
        </div>
      </div>
    </template>
    <template v-else-if="pastDueSubscriptions.length === 0">
      <div class="mt-3">
        <list boxed>
          <list-item>
            <template slot="left">
              <span class="v-list-item__title">
                No Active Subscriptions
              </span>
            </template>
          </list-item>
        </list>
      </div>
    </template>

    <list boxed heading="Payments" class="mt-3">
      <list-item
        v-if="subscriptions.length || pastDueSubscriptions.length"
        data-cy="update-payment-method"
        @click="() => promptUpdateMethod()"
      >
        <template slot="left">
          <v-icon icon="credit_score" />
        </template>
        <div class="v-list-item__title --centered ml-2">
          Update Payment Method
        </div>
      </list-item>
      <list-item chevron @click="() => $router.push('/me/account/purchases')">
        <template slot="left">
          <v-icon icon="attach_money" />
        </template>
        <div class="v-list-item__title --centered ml-2">
          View payment history
        </div>
      </list-item>
    </list>

    <list class="mt-2" boxed heading="Support">
      <list-item chevron @click="() => $router.push('/me/account/support')">
        <template slot="left">
          <v-icon icon="help_outline" />
        </template>
        <div class="v-list-item__title --centered ml-1">
          Get Help
        </div>
      </list-item>
    </list>

    <template v-if="subscriptionToCancel">
      <alert-dialog
        :actions="[
          {
            title: 'No',
            callback: () => (this.showCancelPrompt = false)
          },
          {
            title: 'Yes, cancel',
            callback: cancelSubscription
          }
        ]"
        :show="showCancelPrompt"
        @close="() => (this.showCancelPrompt = false)"
        centered
      >
        <v-icon icon="error_outline" size="4rem" />
        <p class="font-semi-bold">
          Are you sure you want to cancel this subscription?
        </p>
      </alert-dialog>
    </template>

    <v-modal @close="handlePaymentMethodModalClose">
      <template slot="#update-payment-method">
        <div class="p-2 mt-2">
          <h2 class="mt-1 mb-1">Update Payment Method</h2>
          <p class="lead mt-0">
            This will be used for future payments.
          </p>
          <payment-element
            :intent-type="intentType"
            :intent-client-secret="intentClientSecret"
            :amount="intentAmount"
            :currency="intentCurrency"
            :product-id="intentProductId"
            :confirmText="
              !!this.subscriptionToUpdatePaymentMethod
                ? null
                : 'Update Payment Method'
            "
            @back="handlePaymentMethodModalClose"
          />
        </div>
      </template>
    </v-modal>

    <alert-dialog
      :actions="[
        {
          title: 'Continue',
          callback: () => (showSuccessDialog = false)
        }
      ]"
      :show="showSuccessDialog"
      @close="() => (showSuccessDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-semi-bold">
        Thank you for your purchase.
      </p>
    </alert-dialog>
  </v-page>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"

import { isIos, isAppStore } from "@/lib/is-platform"
import ApiClient from "@/lib/api-client"
import { toDate, timeAgo } from "@/lib/date-utils"
import useStripe from "@/lib/mixins/use-stripe"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { openIapManager } from "@/lib/app-store-utils"

import Toolbar from "@/components/Toolbar"
import List from "@/components/List"
import ListItem from "@/components/ListItem"
import AlertDialog from "@/components/AlertDialog"

export default {
  components: {
    Toolbar,
    List,
    ListItem,
    AlertDialog,
    PaymentElement: () => import("@/components/checkout/PaymentElement")
  },

  data() {
    return {
      subscriptions: [],
      pastDueSubscriptions: [],
      paymentMethod: null,
      shouldRetryPayment: false,
      isLoading: false,
      isRetrying: false,
      isUpdatingPaymentMethod: false,
      showCancelPrompt: false,
      showSuccessDialog: false,
      subscriptionToCancel: null,
      subscriptionToUpdatePaymentMethod: null,

      intentType: null,
      intentClientSecret: null,
      intentAmount: null,
      intentCurrency: null,
      intentProductId: null
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) this.loadPage()
      }
    }
  },

  mixins: [useStripe()],

  computed: {
    ...mapState("products", [
      { globalPastDueSubscriptions: "pastDueSubscriptions" }
    ]),
    ...mapGetters("auth", ["isAuthenticated"])
  },

  mounted() {
    EventBus.$on(EVENTS.CHECKOUT_COMPLETED, this.loadPage)
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.CHECKOUT_COMPLETED, this.loadPage)
  },

  methods: {
    toDate,
    timeAgo,

    ...mapActions("products", ["paySubscription", "updatePaymentMethod"]),
    ...mapActions("auth", [
      "fetchAccountSubscriptions",
      "cancelAccountSubscription"
    ]),
    ...mapMutations("products", ["setPastDueSubscriptions"]),

    loadPage() {
      this.isLoading = true
      this.fetchAccountSubscriptions().then(res => {
        this.subscriptions = res.subscriptions
        this.pastDueSubscriptions = res.past_due_subscriptions
        this.isLoading = false
      })
    },

    promptToCancel(subscription) {
      this.subscriptionToCancel = subscription
      this.showCancelPrompt = true
    },

    cancelSubscription() {
      if (this.subscriptionToCancel.is_iap_subscription) {
        openIapManager()

        setTimeout(() => {
          this.subscriptionToCancel = null
        }, 1000)
      } else {
        this.isLoading = true
        this.showCancelPrompt = false
        this.cancelAccountSubscription(this.subscriptionToCancel.id)
          .then(this.loadPage)
          .then(() => {
            if (this.globalPastDueSubscriptions) {
              this.setPastDueSubscriptions(
                this.globalPastDueSubscriptions.filter(
                  subscription =>
                    subscription.id !== this.subscriptionToCancel.id
                )
              )
            }

            this.subscriptionToCancel = null
          })
      }
    },

    retryPayment(subscription) {
      this.isRetrying = true

      this.paySubscription(subscription.id)
        .then(() => {
          this.isRetrying = false
          this.showSuccessDialog = true
          this.loadPage()
          EventBus.$emit(EVENTS.CHECKOUT_COMPLETED, subscription.product.id)
        })
        .catch(err => {
          if (
            err.response.data?.code &&
            err.response.data.code === "invoice_payment_intent_requires_action"
          ) {
            setTimeout(() => {
              this.isRetrying = true
            }, 160)

            this.useStripe_stripe
              .confirmCardPayment(err.response.data.clientSecret)
              .then(response => {
                if (response.error) {
                  this.$notify(response.error.message)
                } else {
                  this.$notify("Payment Successful")
                  EventBus.$emit(
                    EVENTS.CHECKOUT_COMPLETED,
                    subscription.product.id
                  )

                  setTimeout(() => {
                    this.loadPage()
                  }, 1000) // Some time for webhooks to process
                }
              })
              .finally(() => {
                this.isRetrying = false
              })
          } else {
            this.$notify(err.response.data.message)
          }
        })
        .finally(() => {
          this.isRetrying = false
        })
    },

    promptUpdateMethod(subscription = null, retryPayment = false) {
      if (isIos() && isAppStore()) {
        return openIapManager()
      } else if (subscription) {
        this.subscriptionToUpdatePaymentMethod = subscription
        this.shouldRetryPayment = retryPayment

        ApiClient.get(`/subscriptions/${subscription.id}/payment_intent`).then(
          ({ data }) => {
            this.intentType = data.intentType
            this.intentClientSecret = data.clientSecret
            this.intentAmount = data.amount
            this.intentCurrency = data.currency
            this.intentProductId = data.productId
          }
        )
      } else {
        ApiClient.get(`/subscriptions/setup_intent`).then(({ data }) => {
          this.intentType = data.intentType
          this.intentClientSecret = data.clientSecret
        })
      }

      this.$router.push("#update-payment-method")
    },

    async attemptUpdatePaymentMethod() {
      this.isUpdatingPaymentMethod = true

      try {
        const {
          paymentMethod
        } = await this.useStripe_stripe.createPaymentMethod({
          type: "card",
          ...this.paymentMethod.paymentMethod
        })

        this.updatePaymentMethod({
          subscriptionId: this.subscriptionToUpdatePaymentMethod.id,
          paymentMethodId: paymentMethod.id
        })
          .then(() => {
            this.$router.removeHash()
            if (this.shouldRetryPayment) {
              this.retryPayment(this.subscriptionToUpdatePaymentMethod)
              this.shouldRetryPayment = false
            } else {
              this.$notify("Successfully Updated Payment Method")
            }
            this.subscriptionToUpdatePaymentMethod = null
          })
          .catch(err => {
            this.$notify(err.response.data.message)
          })
          .finally(() => {
            this.isUpdatingPaymentMethod = false
          })
      } catch (err) {
        console.error(err)
        this.$rollbar.critical(err)
        this.$notify("Unable to update payment method")
        this.isUpdatingPaymentMethod = false
      }
    },

    handlePaymentMethodModalClose() {
      this.intentType = null
      this.intentClientSecret = null
      this.intentType = null
      this.intentClientSecret = null
      this.intentAmount = null
      this.intentCurrency = null
      this.intentProductId = null

      this.subscriptionToUpdatePaymentMethod = null
      this.$router.removeHash()
    }
  }
}
</script>
